/**
 * Module de rendu des maisons
 */
import { wheelAngle, arabicToRoman } from '../core/utils.js';

export class HouseRenderer {
    /**
     * @param {CanvasRenderingContext2D} ctx - Contexte 2D du canvas
     * @param {Object} options - Options de configuration
     */
    constructor(ctx, options) {
        this.ctx = ctx;
        this.options = options;
    }

    /**
     * Dessine les cusps des maisons
     * @param {number} cx - Coordonnée X du centre
     * @param {number} cy - Coordonnée Y du centre
     * @param {number} radius - Rayon du cercle extérieur
     * @param {Array} houses - Données des maisons
     */
    draw(cx, cy, radius, houses) {
        if (!houses || !houses.length) return;

        const ctx = this.ctx;
        const innerRadius = radius * this.options.innerRadius;

        // Dessiner chaque cusp comme une ligne et un point
        houses.forEach(house => {
            if (!house) return;

            const angle = wheelAngle(house.position, this.options.ascendant);

            // Ligne de la maison - Style Art Déco
            ctx.beginPath();
            ctx.moveTo(
                cx + innerRadius * Math.cos(angle),
                cy - innerRadius * Math.sin(angle)
            );
            ctx.lineTo(
                cx + radius * 0.9 * Math.cos(angle),
                cy - radius * 0.9 * Math.sin(angle)
            );
            ctx.strokeStyle = 'rgba(160, 136, 119, 0.6)';  // ink-muted
            ctx.lineWidth = 1.2;
            ctx.stroke();

            // Point de la cusp avec style Art Déco
            ctx.beginPath();
            ctx.arc(
                cx + radius * 0.9 * Math.cos(angle),
                cy - radius * 0.9 * Math.sin(angle),
                3, 0, Math.PI * 2
            );
            // The cusp dot itself, so it must READ against the cream ground — a surface
            // colour here would simply erase it.
            ctx.fillStyle = '#d85f28';  // fox, deep
            ctx.fill();
            // Bordure pour le point
            ctx.strokeStyle = 'rgba(230, 191, 164, 0.8)';  // line-strong
            ctx.lineWidth = 1;
            ctx.stroke();

            // Numéro de maison en chiffres romains - Style Art Déco
            const labelRadius = innerRadius * 0.75;
            const fontSize = Math.max(16, Math.round(radius * 0.05));
            // The theme's display face. It was "Cinzel", which this theme never loads —
            // the numbers fell back to whatever generic serif the browser had.
            ctx.font = `700 ${fontSize}px "Space Grotesk", "Inter", system-ui, sans-serif`;
            ctx.fillStyle = '#53615c';  // ink-soft
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Trouver la position de la prochaine maison pour placer le numéro au milieu
            const nextIdx = house.number % 12;
            const nextHouse = houses[nextIdx];

            if (nextHouse) {
                let midPos;

                // Gérer le cas où la maison traverse 0°
                if (nextHouse.position < house.position) {
                    midPos = (house.position + (nextHouse.position + 360)) / 2;
                    if (midPos >= 360) midPos -= 360;
                } else {
                    midPos = (house.position + nextHouse.position) / 2;
                }

                const midAngle = wheelAngle(midPos, this.options.ascendant);

                // Convertir le numéro en chiffre romain
                const romanNumeral = arabicToRoman(house.number);

                ctx.fillText(
                    romanNumeral,
                    cx + labelRadius * Math.cos(midAngle),
                    cy - labelRadius * Math.sin(midAngle)
                );
            }
        });
    }
}